import {LoadingStatus, status} from 'amc-common-components';
import {createFeatureSelector, createSelector} from '@ngrx/store';
import {AppState} from '@store';
import {createStateSelector} from 'amc-common-components';

/**
 * Get Combined Loading Statuses
 * @param {LoadingStatus[]} statuses
 * @returns {LoadingStatus}
 */
export function combineLoadingStatuses(...statuses: LoadingStatus[]): LoadingStatus {
	const errorStatus = statuses.find((loadingStatus) => loadingStatus.error);

	if (errorStatus) {
		return status.error(errorStatus.error);
	}

	statuses = statuses.filter(() => status.loading || status.loaded);

	if (statuses.find((loadingStatus) => loadingStatus.loading)) {
		return status.loading;
	}

	if (statuses.every((loadingStatus) => loadingStatus.loaded)) {
		return status.loaded;
	}

	return status.default;
}

/**
 * Get Combined Loadings Only
 * @param {LoadingStatus} mainStatus
 * @param {LoadingStatus[]} additionalStatuses
 * @returns {LoadingStatus}
 */
export function combineLoadingsOnly(mainStatus: LoadingStatus, ...additionalStatuses: LoadingStatus[]): LoadingStatus {
	const areAdditionalLoadingsInProgress = additionalStatuses.some((st) => st.loading);
	return mainStatus.loading || areAdditionalLoadingsInProgress ? status.loading : mainStatus;
}

/**
 * Create Combined Loading Status Selector
 * @param statusSelectors
 * @returns {void}
 */
export function createCombinedLoadingStatusSelector(...statusSelectors) {
	return createSelector.apply(null, [...statusSelectors, combineLoadingStatuses]);
}

/**
 * Create Combined By Loading Status Selector
 * @param mainSelector
 * @param additionalSelectors
 * @returns {void}
 */
export function createCombinedByLoadingLoadingStatusSelector(mainSelector, ...additionalSelectors) {
	return createSelector.apply(null, [mainSelector, ...additionalSelectors, combineLoadingsOnly]);
}

/**
 * Get Store Selector
 * @param {FirstKey} firstProp
 * @returns {void}
 */
export function getStoreSelector<FirstKey extends keyof State, State = AppState>(firstProp: FirstKey) {
	const getOutState = createFeatureSelector<State[FirstKey]>(firstProp as string);
	return <SecondKey extends keyof State[FirstKey]>(secondProp: SecondKey) =>
		createStateSelector(getOutState, secondProp);
}
